import React, { useState, useEffect } from 'react';
import GeneratorPanel from './GeneratorPanel';
import IntroPanel from './IntroPanel';
import generate from './MandGen';
import coordId from './coordId';
import { toCanvasPoint } from './pointer';
import { computeCanvasDimensions, fitWholeSet } from './renderSize';
import useGallery from './useGallery';
import { renderThumb } from './thumbs';
import yieldToPaint from './yieldToPaint';

// `explore` is the /explore route: the visitor has already committed (they came from
// a share link, their gallery, or a 404), so the pitch would be asking them the same
// question twice. It skips straight to the first render.
//
// It selects the view outright rather than merely triggering a render — see the
// return statement for why that distinction is load-bearing.
const Display = ({ explore = false, onHistoryChange }) => {

    const [canvasDims, setCanvasDims] = useState(computeCanvasDimensions);

    const cw = canvasDims.width;
    const ch = canvasDims.height;

    const [zooms, setZooms] = useState([]);
    const [display, setDisplay] = useState(null);

    // Stored as single-element arrays and mutated in place by the drag handlers
    // (a deliberate quirk of this code); the state setters are intentionally unused.
    const [topLeftX] = useState([0]);
    const [topLeftY] = useState([0]);
    const [bottomRightX] = useState([cw]);
    const [bottomRightY] = useState([ch]);

    const startBounds = fitWholeSet(window.innerWidth / window.innerHeight);
    const [minr, setMinr] = useState([startBounds.realMin]);
    const [maxr, setMaxr] = useState([startBounds.realMax]);
    const [mini, setMini] = useState([startBounds.imaginaryMin]);
    const [maxi, setMaxi] = useState([startBounds.imaginaryMax]);
    const [dragMode, setDragMode] = useState(false);
    const [graphicsCtx, setGraphicsCtx] = useState(null);

    const { save, remove, isSaved } = useGallery();
    const [saveError, setSaveError] = useState(false);

    // Set only if the opening render could not be produced at all, so /explore says so
    // instead of sitting on "Drawing…" forever.
    const [startFailed, setStartFailed] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const dims = computeCanvasDimensions();
            setCanvasDims(dims);
            // Before the first render, keep the opening view fitted to the
            // viewport so Begin frames the whole set correctly. After the user
            // has started zooming, their region is preserved (it re-fits to the
            // canvas aspect on the next zoom).
            if (zooms.length === 0) {
                const b = fitWholeSet(dims.width / dims.height);
                setMinr([b.realMin]);
                setMaxr([b.realMax]);
                setMini([b.imaginaryMin]);
                setMaxi([b.imaginaryMax]);
            }
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [zooms.length]);

    const zoom = () => {
        let zoomResponse = generate({
            manual: false,
            width: cw,
            height: ch,
            mousePositions: {
                tlx: topLeftX[0],
                tly: topLeftY[0],
                brx: bottomRightX[0],
                bry: bottomRightY[0]
            },
            axisParams: {
                realMin: minr[0],
                realmax: maxr[0],
                imaginaryMin: mini[0],
                imaginaryMax: maxi[0]
            }
        });
        applyZoom(zoomResponse);
    };

    // First render from the hero: frame the whole current (fitted) region.
    const beginRender = () => {
        topLeftX[0] = 0;
        topLeftY[0] = 0;
        bottomRightX[0] = cw;
        bottomRightY[0] = ch;
        zoom();
    };

    // /explore: the same first render, without the click. It has to be pushed off
    // the paint path — renderRegion is seconds of synchronous work, and running it
    // during the route transition would leave the *previous* page frozen on screen,
    // which is a worse arrival than the button we removed.
    //
    // Keyed on `explore`, NOT on mount, and this is the whole subtlety: `/` and
    // `/explore` both render <Home>, so navigating between them reconciles the same
    // Display instance rather than remounting it. The prop flips false -> true on a
    // component that is already alive. A mount-only effect never re-runs (it already
    // returned early), and useState's initial value is ignored on re-render — so the
    // button changed the URL and nothing else, while a refresh worked, because that
    // *is* a fresh mount.
    useEffect(() => {
        if (!explore) return undefined;
        // Already exploring: /explore has nothing to add, and re-running the opening
        // render would throw away the zoom the visitor came back to.
        if (zooms.length > 0) return undefined;

        let cancelled = false;
        setStartFailed(false);

        (async () => {
            await yieldToPaint();
            if (cancelled) return;
            try {
                beginRender();
            } catch {
                if (!cancelled) setStartFailed(true);
            }
        })();

        return () => { cancelled = true; };
        // beginRender closes over the current canvas dimensions, which are the ones
        // the opening view was last fitted to; zooms.length is read as the value at
        // the moment `explore` flipped, which is exactly the guard we want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [explore]);

    const applyZoom = (zoomResponse) => {
        setDisplay(zoomResponse.dataUrl);
        setZooms(prev => [...prev, zoomResponse]);
        setMinr([zoomResponse.realMin]);
        setMaxr([zoomResponse.realMax]);
        setMini([zoomResponse.imaginaryMin]);
        setMaxi([zoomResponse.imaginaryMax]);
    };

    const reloadZoom = (oldZoom) => {
        setDisplay(oldZoom.dataUrl);
        setMinr([oldZoom.realMin]);
        setMaxr([oldZoom.realMax]);
        setMini([oldZoom.imaginaryMin]);
        setMaxi([oldZoom.imaginaryMax]);
    };

    const manualZoom = () => {
        const manualMinR = document.getElementById('real-min-in').valueAsNumber;
        const manualMaxR = document.getElementById('real-max-in').valueAsNumber;
        const manualMinI = document.getElementById('imaginary-min-in').valueAsNumber;
        const manualMaxI = document.getElementById('imaginary-max-in').valueAsNumber;

        if ([manualMinR, manualMaxR, manualMinI, manualMaxI].some(v => Number.isNaN(v))) {
            return;
        }

        const response = generate({
            manual: true,
            width: cw,
            height: ch,
            axisValues: {
                newMinR: manualMinR,
                newMaxR: manualMaxR,
                newMinI: manualMinI,
                newMaxI: manualMaxI
            }
        });
        applyZoom(response);
    };

    // Always measure the tracker canvas (a single fixed element), never the
    // event target — a pointer can land on either the <img> or the overlapping
    // canvas, and those must not give different reference rects. Convert the
    // screen point into canvas pixels here so everything downstream works in one
    // space, regardless of display size.
    const toCanvas = (clientX, clientY) => {
        const rect = document.getElementById('tracker').getBoundingClientRect();
        return toCanvasPoint(clientX, clientY, rect, cw, ch);
    };

    // Lock the selection to the canvas (=viewport) aspect ratio around the start
    // corner, so the boxed region always renders without distortion — the
    // viewfinder shows exactly the framing you'll get.
    const lockedCorner = (x0, y0, x, y) => {
        const aspect = cw / ch;
        const dx = x - x0;
        const dy = y - y0;
        let w = Math.abs(dx);
        let h = Math.abs(dy);
        if (h === 0 || w / h > aspect) {
            h = w / aspect;
        } else {
            w = h * aspect;
        }
        const cx = Math.min(Math.max(x0 + (dx < 0 ? -w : w), 0), cw);
        const cy = Math.min(Math.max(y0 + (dy < 0 ? -h : h), 0), ch);
        return { x: cx, y: cy };
    };

    const drawViewfinder = (x0, y0, x1, y1) => {
        const ctx = graphicsCtx;
        ctx.clearRect(0, 0, cw, ch);

        const left = Math.min(x0, x1);
        const right = Math.max(x0, x1);
        const top = Math.min(y0, y1);
        const bottom = Math.max(y0, y1);

        ctx.strokeStyle = '#ECE7DC';
        ctx.lineWidth = Math.max(1, cw / 800);

        // faint framing rectangle
        ctx.globalAlpha = 0.5;
        ctx.strokeRect(left, top, right - left, bottom - top);

        // crisp corner ticks
        ctx.globalAlpha = 1;
        const t = Math.min(right - left, bottom - top) * 0.18;
        ctx.beginPath();
        ctx.moveTo(left, top + t); ctx.lineTo(left, top); ctx.lineTo(left + t, top);
        ctx.moveTo(right - t, top); ctx.lineTo(right, top); ctx.lineTo(right, top + t);
        ctx.moveTo(right, bottom - t); ctx.lineTo(right, bottom); ctx.lineTo(right - t, bottom);
        ctx.moveTo(left + t, bottom); ctx.lineTo(left, bottom); ctx.lineTo(left, bottom - t);
        ctx.stroke();
    };

    const beginDrag = (clientX, clientY) => {
        const { x, y } = toCanvas(clientX, clientY);
        topLeftX[0] = x;
        topLeftY[0] = y;

        const ctx = document.getElementById('tracker').getContext('2d');
        setGraphicsCtx(ctx);
        setDragMode(true);
    };

    const moveDrag = (clientX, clientY) => {
        if (!dragMode || !graphicsCtx) return;
        const { x, y } = toCanvas(clientX, clientY);
        const c = lockedCorner(topLeftX[0], topLeftY[0], x, y);
        bottomRightX[0] = c.x;
        bottomRightY[0] = c.y;
        drawViewfinder(topLeftX[0], topLeftY[0], c.x, c.y);
    };

    const endDrag = (clientX, clientY) => {
        if (!dragMode) return;
        const { x, y } = toCanvas(clientX, clientY);
        const c = lockedCorner(topLeftX[0], topLeftY[0], x, y);
        bottomRightX[0] = c.x;
        bottomRightY[0] = c.y;

        setDragMode(false);
        if (graphicsCtx) graphicsCtx.clearRect(0, 0, cw, ch);

        // Ignore a tap / zero-area drag (easy on touch) — it would zoom to a
        // degenerate region and paint nothing.
        if (Math.abs(c.x - topLeftX[0]) < 3 || Math.abs(c.y - topLeftY[0]) < 3) return;

        zoom();
    };

    const handleMouseDown = (event) => {
        event.preventDefault();
        beginDrag(event.clientX, event.clientY);
    };

    const handleMouseMove = (event) => {
        moveDrag(event.clientX, event.clientY);
    };

    const handleMouseRelease = (event) => {
        event.preventDefault();
        endDrag(event.clientX, event.clientY);
    };

    const handleTouchStart = (event) => {
        event.preventDefault();
        beginDrag(event.touches[0].clientX, event.touches[0].clientY);
    };

    const handleTouchMove = (event) => {
        event.preventDefault();
        moveDrag(event.touches[0].clientX, event.touches[0].clientY);
    };

    const handleTouchEnd = (event) => {
        event.preventDefault();
        endDrag(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
    };

    const current = {
        realMin: minr[0],
        realMax: maxr[0],
        imaginaryMin: mini[0],
        imaginaryMax: maxi[0]
    };
    current.id = coordId(current);

    // Where in the history the view currently sits — derived from the bounds rather
    // than tracked, exactly as the filmstrip's active frame is, so the two can never
    // disagree about where you are.
    const activeIndex = zooms.findIndex(z => z.id === current.id);
    const canGoBack = activeIndex > 0;

    // Back is the answer to an accidental zoom, which is why it is one step and not
    // a reset: it also covers the far commoner "that turn was dull", and it can never
    // cost more than the last thing you did. The filmstrip remains the way to jump
    // anywhere. Reported upward because the nav bar is Display's sibling, not its
    // child — the recovery control belongs beside the wordmark, where the instinct
    // to start over already sends people.
    useEffect(() => {
        if (!onHistoryChange) return;
        onHistoryChange({
            canGoBack,
            goBack: canGoBack ? () => reloadZoom(zooms[activeIndex - 1]) : null
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canGoBack, activeIndex, zooms]);

    // Save keeps only the coordinates — the image is re-derivable, so this is
    // ~100 bytes (see gallery.js). The thumbnail is generated afterwards, on a
    // later tick, so the click reads as instant: renderThumb is a real (if small)
    // synchronous render and must not sit between the tap and the "Saved" label.
    //
    // Since Block I this is async, because a signed-in save goes to D1. The label
    // still flips immediately — the remote store updates optimistically and only
    // reverts if the server refuses — so what we await here is the CONFIRMATION,
    // not the feedback.
    const toggleSave = async () => {
        const bounds = {
            realMin: current.realMin,
            realMax: current.realMax,
            imaginaryMin: current.imaginaryMin,
            imaginaryMax: current.imaginaryMax
        };

        if (isSaved(current.id)) {
            setSaveError(!(await remove(current.id)));
            return;
        }

        const result = await save(bounds);
        setSaveError(!result.ok);
        if (!result.ok) return;

        window.setTimeout(() => {
            try {
                renderThumb(bounds);
            } catch {
                /* a missing thumbnail costs a re-render later, nothing more */
            }
        }, 0);
    };

    // Which view appears is decided by the ROUTE, not by whether a render happens to
    // be in state. `/` and `/explore` share this component instance (see the effect
    // above), so a state-driven choice meant the generator stayed on screen after
    // navigating home — the wordmark changed the URL and nothing else. The zoom state
    // survives that navigation on purpose: going to look at the front page should not
    // destroy an exploration, so coming back to /explore resumes it.
    return (
        <div id="main-display">
            {!explore
                ? <IntroPanel />
                : zooms.length > 0
                ? <GeneratorPanel
                        zooms={zooms}
                        display={display}
                        current={current}
                        saved={isSaved(current.id)}
                        saveError={saveError}
                        onToggleSave={toggleSave}
                        cw={cw}
                        ch={ch}
                        handleMouseDown={handleMouseDown}
                        handleMouseRelease={handleMouseRelease}
                        handleMouseMove={handleMouseMove}
                        handleTouchStart={handleTouchStart}
                        handleTouchMove={handleTouchMove}
                        handleTouchEnd={handleTouchEnd}
                        reloadZoom={reloadZoom}
                        manualZoom={manualZoom} />
                : <div id="starting">
                      {startFailed
                          ? /* The pitch is not the fallback any more: its button links
                               to this route, so falling back to it would be a loop. */
                            <p className="render-note is-centred is-error" role="status">
                                This browser couldn&rsquo;t draw the set.
                            </p>
                          : /* Names what is about to appear, because an /explore
                               arrival is expecting a deep zoom and gets the wide
                               view of the whole set to start from. */
                            <p className="render-note is-centred" role="status">Drawing the whole set&hellip;</p>
                      }
                  </div>}
        </div>
    );
};

export default Display;
